import React from 'react';
import { Award, Lightbulb, Shield, Users, Clock } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award size={24} />,
      title: 'Quality',
      description: 'Premium materials and meticulous services'
    },
    {
      icon: <Lightbulb size={24} />,
      title: 'Innovation',
      description: 'Creative thinking design'
    },
    {
      icon: <Shield size={24} />,
      title: 'Integrity',
      description: 'Transparency and honesty in every relationship'
    },
    {
      icon: <Users size={24} />,
      title: 'Customer Focus',
      description: 'Designs that suit unique lifestyles'
    },
    {
      icon: <Clock size={24} />,
      title: 'Efficiency',
      description: 'Meeting deadlines without compromise'
    }
  ];

  const stats = [
    { number: '5+', label: 'Years' },
    { number: '100+', label: 'Projects' },
    { number: '100%', label: 'Custom Designs' }
  ];

  return (
    <section id="about" className="py-20 sm:py-28 lg:py-32 bg-warm-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Lifestyle story section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 sm:mb-28 lg:mb-32">
          {/* Large lifestyle image */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_design-crafters-2/artifacts/885fctx6_624623072_781554964987700_1951070273906982112_n.jpg"
                alt="Kensetsu design excellence"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Floating accent element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gold/10 rounded-2xl -z-10"></div>
          </div>

          {/* Story content */}
          <div className="space-y-6 sm:space-y-8">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-playfair font-light text-navy leading-tight mb-4 sm:mb-6">
                Designed for<br />
                <span className="font-medium">Elegant Living</span>
              </h2>
              <div className="w-12 sm:w-16 h-0.5 bg-burgundy mb-6 sm:mb-8"></div>
            </div>

            <p className="text-base sm:text-lg text-warm-charcoal/80 leading-relaxed-xl font-manrope">
              Our work begins long before installation. Every cabinet, every finish, every detail is thoughtfully designed to elevate how you live.
            </p>

            <div className="pt-4">
              <h3 className="text-xl sm:text-2xl font-playfair font-medium text-navy mb-4">
                Our Story
              </h3>
              <p className="text-base sm:text-lg text-warm-charcoal/80 leading-relaxed-xl font-manrope">
                Kensetsu Limited emerged from a vision to bring contemporary, space smart furnishings to Jamaica. We combine elegant modern design with the warmth and individuality that make every living space unique.
              </p>
            </div>

            <p className="text-base sm:text-lg text-warm-charcoal/80 leading-relaxed-xl font-manrope">
              From concept to completion, we manage every stage internally, using advanced woodworking technology and premium materials. The result: bespoke interiors that reflect your individuality.
            </p>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 pt-6 sm:pt-8 border-t border-sand">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-playfair font-medium text-burgundy mb-1 sm:mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs sm:text-sm font-manrope text-warm-charcoal/70 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mission & Vision - refined cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-32">
          <div className="bg-navy p-12 rounded-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-burgundy/10 rounded-full -mr-32 -mt-32 group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-playfair font-light text-warm-white mb-6">
                Our Mission
              </h3>
              <p className="text-warm-white/80 leading-relaxed-xl font-manrope text-lg">
                To elevate modern living by designing contemporary, smart furnishings that reflect our clients' individuality while upholding the highest standards of quality and innovation.
              </p>
            </div>
          </div>

          <div className="bg-burgundy p-12 rounded-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full -mr-32 -mt-32 group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-playfair font-light text-warm-white mb-6">
                Our Vision
              </h3>
              <p className="text-warm-white/80 leading-relaxed-xl font-manrope text-lg">
                To become Jamaica's leading provider of modern, customised furnishings known for design excellence and service that consistently exceeds expectations.
              </p>
            </div>
          </div>
        </div>

        {/* Values - minimal elegant cards */}
        <div className="text-center mb-16">
          <h3 className="text-4xl font-playfair font-light text-navy mb-4">
            What Guides Us
          </h3>
          <div className="w-16 h-0.5 bg-gold mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className="group text-center space-y-4 p-6 rounded-xl hover:bg-sand/30 transition-all duration-300"
            >
              <div className="w-16 h-16 mx-auto bg-sand-light rounded-full flex items-center justify-center text-burgundy group-hover:bg-burgundy group-hover:text-warm-white transition-all duration-300">
                {value.icon}
              </div>
              <h4 className="text-lg font-manrope font-semibold text-navy">
                {value.title}
              </h4>
              <p className="text-sm text-warm-charcoal/70 font-manrope leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>

        {/* Turnaround time highlight */}
        <div className="mt-32 text-center max-w-3xl mx-auto">
          <div className="bg-sand/50 p-12 rounded-2xl border border-gold/20">
            <p className="text-2xl font-playfair text-navy leading-relaxed">
              We pride ourselves on efficient turnaround time, allowing you to plan confidently while receiving the best in design.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
